import '../OrderWizard.scss';
import './OrderCheckEligibility.scss';

import { FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { Button } from '@myome/ui';

import { FormPanel } from '@components/FormPanel/FormPanel';
import { TurnstileAnchor } from '@components/Turnstile/TurnstileSlot';

import { EligibilityFormFields } from '../components/EligibilityFormFields';
import Stepper from '../components/Stepper';
import { useEligibilitySubmission } from '../hooks/useEligibilitySubmission';
import { useOrderContext } from '../hooks/useOrderContext';
import { NotGuardianModal } from './components/NotGuardianModal';
import { getEligibilityFormSchema, getInitialValues } from './OrderCheckEligibility.validation';

export default function OrderCheckEligibility() {
  const { partnerConfig, dispatch, resetEligibility } = useOrderContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const hasCollapsedHistoryRef = useRef(false);
  const [showNotGuardianModal, setShowNotGuardianModal] = useState(false);

  const allowsMinorWithGuardian = !!partnerConfig?.allowsMinorWithGuardian;
  const validationSchema = useMemo(
    () => getEligibilityFormSchema({ allowsMinorWithGuardian }),
    [allowsMinorWithGuardian],
  );

  // When returning from "Leave Page" on the wizard: collapse history so one back goes to get-started.
  // go(-2) moves us to the first check-eligibility entry; the browser forward button may remain
  // available (History API does not allow both no-forward and single-back).
  useEffect(() => {
    const fromLeaveWizard = (location.state as { fromLeaveWizard?: boolean } | null)?.fromLeaveWizard;
    if (fromLeaveWizard && !hasCollapsedHistoryRef.current) {
      hasCollapsedHistoryRef.current = true;
      window.history.go(-2);
    }
  }, [location.state]);

  // Reset eligibility flag and clear wizard state when component mounts
  // This prevents users from bypassing the eligibility check by navigating back
  // and then manually entering the wizard URL
  useEffect(() => {
    resetEligibility();
  }, [resetEligibility]);

  // Get promoCode from URL query param
  const promoCodeFromUrl = searchParams.get('promo_code') || '';

  // Use eligibility submission hook
  const { submitEligibilityCheck } = useEligibilitySubmission({ dispatch });

  const headerTitle = allowsMinorWithGuardian ? 'Enter patient information' : 'Enter your information';
  const headerSubtitle = allowsMinorWithGuardian
    ? "We'll confirm eligibility to continue ordering"
    : "We'll confirm your eligibility to continue ordering";

  const formik = useFormik({
    initialValues: getInitialValues(promoCodeFromUrl),
    validationSchema,
    onSubmit: submitEligibilityCheck,
  });

  // partnerConfig loads async for API-only partners (e.g. BridgeBio); re-run validation when the
  // schema flips so a minor DOB validated under the 18+ rule does not keep a stale dob error.
  const prevAllowsMinorWithGuardianRef = useRef<boolean | null>(null);
  const { validateForm } = formik;
  useEffect(() => {
    if (prevAllowsMinorWithGuardianRef.current === null) {
      prevAllowsMinorWithGuardianRef.current = allowsMinorWithGuardian;
      return;
    }
    if (prevAllowsMinorWithGuardianRef.current !== allowsMinorWithGuardian) {
      prevAllowsMinorWithGuardianRef.current = allowsMinorWithGuardian;
      validateForm().catch(() => undefined);
    }
  }, [allowsMinorWithGuardian, validateForm]);

  const isSubmitDisabled = formik.isSubmitting || !formik.isValid || formik.values.boneMarrowTransplant === 'YES';

  return (
    <div className='wizard-page'>
      <FormPanel maxWidth='lg' layout='sectioned'>
        <FormPanel.Header title={headerTitle} subtitle={headerSubtitle} />
        <div className='form-panel__stepper-slot'>
          <Stepper total={5} current={0} />
        </div>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} noValidate>
            <FormPanel.Body>
              <EligibilityFormFields
                formik={formik}
                showPromoCodeField={!partnerConfig || !!partnerConfig.usesPromoCode}
                allowsMinorWithGuardian={allowsMinorWithGuardian}
                onNotGuardianClick={() => setShowNotGuardianModal(true)}
              />
            </FormPanel.Body>
            <FormPanel.Footer
              variant='actions'
              after={
                <div className='form-panel__footer-actions form-panel__footer-actions--end mt-3'>
                  <TurnstileAnchor />
                </div>
              }
            >
              <Button
                type='submit'
                variant='primary'
                disabled={isSubmitDisabled}
                ariaLabel={formik.isSubmitting ? 'Checking eligibility' : 'Continue to next step'}
              >
                {formik.isSubmitting ? 'Checking…' : 'Continue'}
              </Button>
            </FormPanel.Footer>
          </form>
        </FormikProvider>
      </FormPanel>
      <NotGuardianModal
        show={showNotGuardianModal}
        onClose={() => setShowNotGuardianModal(false)}
        firstName={formik.values.firstName}
      />
    </div>
  );
}
